import React, {useContext, useEffect, useState} from 'react';
import {default as momentApi} from 'moment/moment';
import classnames from '@src/helpers/classnames';
import {BootstrapColors} from '@src/@types/Bootstrap';
import {getCssVarValue} from '@src/helpers/getCssVarValue';
import trans from '@src/helpers/trans';
import {LocaleContext} from '@src/contexts/LocaleContext';
import trans_choice from '@src/helpers/trans_choice';

export default function CountDown({expiredAt, className, ...props}: {
    expiredAt: momentApi.Moment,
    className?: string,
    badgeColor?: BootstrapColors,
    shouldPulse?: boolean,
    title?: string,
    titlePrefix?: string,
}) {
    const {locale} = useContext(LocaleContext);
    const [milliseconds, setMilliseconds] = useState<number>(Math.abs(expiredAt.valueOf() - Date.now()));

    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;
        timeout = setTimeout(function tick() {
            setMilliseconds(
                    Math.abs(expiredAt.valueOf() - Date.now())
            );

            timeout = setTimeout(tick, 1000);
        }, 1000);

        return () => clearTimeout(timeout);
    }, [expiredAt]);

    const isExpired = Date.now() > expiredAt.valueOf();
    const days = Math.floor(milliseconds / 1000 / 86400);
    const hours = Math.floor(milliseconds / 1000 % 86400 / 3600);
    const minutes = Math.floor(milliseconds / 1000 % 3600 / 60);
    const seconds = Math.floor(milliseconds / 1000 % 60);

    const D = String(days).padStart(2, '0');
    const H = String(hours).padStart(2, '0');
    const M = String(minutes).padStart(2, '0');
    const S = String(seconds).padStart(2, '0');

    const totalHours = days * 24 + hours;

    const color = props.badgeColor ?? classnames({
        'danger': isExpired || totalHours < 1,
        'warning': !isExpired && totalHours >= 1 && totalHours < 4,
        'success': !isExpired && totalHours >= 4,
    });

    const translate = (message?: string, def?: string): string | null => {
        return message ? trans(message, undefined, locale) : (def ?? null);
    };

    const humanTimeMessage = days !== 0 ? (
            isExpired ?
                    'Timer:CountDown.time-with-days-in-past' :
                    'Timer:CountDown.time-with-days'
    ) : (
            isExpired ?
                    'Timer:CountDown.time-in-past' :
                    'Timer:CountDown.time'
    );

    const humanTime = trans_choice(humanTimeMessage, days, {D, H, M, S}, null, locale);

    return <div className={classnames(className, 'time-counter__wrapper')}>
        <span
                className={classnames('time-counter', 'badge', `bg-${color}`, {
                    'with-pulse-animation': props.shouldPulse
                })}
                style={!props.shouldPulse ? undefined : {
                    '--animation-pulse--color': getCssVarValue(`--bs-${color}-rgb`, '0, 0, 0')
                } as React.CSSProperties}
                title={translate(props.title) ?? `${translate(props.titlePrefix, '')}${humanTime}`}
        >
            {isExpired ? expiredAt.fromNow() : <>{humanTime}</>}
        </span>
    </div>;
}
